import * as React from "react";
import { useParams } from "react-router-dom";
import { CalendarDatum, CalendarDayData, CalendarTooltipProps, ColorScale, ResponsiveCalendar } from "@nivo/calendar";
import * as moment from "moment";
import * as classnames from "classnames";
import { DiarySessionResult } from "./DiarySessionResult";
import { DiarySubjectSummaryPanel } from "./DiarySubjectSummaryPanel";
import { DiaryTrendGraph } from "./DiaryTrendGraph";
import { Datum, Point, Serie } from "@nivo/line";
import { WebPortalClient, Subject, DiarySession, Site, DiaryCompliance } from "webportalclient";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { Authorization } from "../../misc/components/Authorization";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { useSecurityContext } from "../../Core/SecurityContext";
import { orderBy, groupBy, maxBy, Dictionary } from "lodash";
import { scaleQuantize } from 'd3-scale';
import { DiaryDaybyDayCalendar, DiaryCalendarDayData, DiaryDaybyDayTimeRange, DiaryDayByDay } from "./DiaryDaybyDayCalendar";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";

enum DiaryResultsTabs {
    PEF = "PEF",
    FEV1 = "FEV1"
}

export const DiaryResults = () => {
    const { id } = useParams<{ id: string }>();

    const [selectedDate, setSelectedDate] = React.useState<moment.Moment>();
    const [currentTab, setCurrentTab] = React.useState<DiaryResultsTabs>(DiaryResultsTabs.PEF);
    const [showFilter, setShowFilter] = React.useState(false);
    const client = useWebPortalApiClient();
    const [site, setSite] = React.useState<Site>();
    const [subject, setSubject] = React.useState<Subject>();
    const [allSessions, setAllSessions] = React.useState<DiarySession[]>();
    const [sessions, setSessions] = React.useState<DiarySession[]>([]);
    const [complianceRecords, setComplianceRecords] = React.useState<DiaryCompliance[]>();
    const [loadingSessions, setLoadingSessions] = React.useState<boolean>(false);

    const { study } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions } = useSecurityContext();

    React.useEffect(() => {
        const getData = async () => {
            const subjectResult = await client.client.getSubject(id);
            setSubject(subjectResult);

            const siteResult = await client.client.getSite(subjectResult.siteId);
            setSite(siteResult);

            const sessionResult = await client.client.getDiarySessions(undefined, `visit/subjectId eq ${id}`, undefined, "tests,questionnaires,visit");
            setAllSessions(sessionResult.value);

            const complianceResult = await client.client.getDiaryCompliances(undefined, `session/visit/subjectId eq ${id}`, "occurred desc");
            setComplianceRecords(complianceResult.value);
        }

        getData();
    }, [client.client, id]);

    const GetScopedSessionData = async () => {
        if (selectedDate) {
            setLoadingSessions(true);

            const endOfDay = moment.tz(selectedDate, moment.tz.guess()).endOf('day').toISOString();
            const sessionResult = await client.client.getDiarySessions(undefined, `visit/subjectId eq ${id} and occurred ge ${moment.tz(selectedDate, moment.tz.guess()).startOf('day').toISOString() } and occurred le ${endOfDay}`, undefined, "visit,tests");
            setSessions(sessionResult.value);

            setLoadingSessions(false);
        }
    }

    React.useEffect(() => {
        GetScopedSessionData();
    }, [client.client, selectedDate, id]);


    const extractHighestFromMultipleSessions = (highestAcceptableGroupedPerDay: Dictionary<Datum[]>): Datum[] => {
        const highestAcceptable: Datum[] = [];
        for (const key in highestAcceptableGroupedPerDay) {
            const sessionsInDay = highestAcceptableGroupedPerDay[key];

            if (sessionsInDay.length === 0)
                highestAcceptable.push(sessionsInDay[0]);
            else {
                const maxValue = orderBy(sessionsInDay, [s => s.accepted, s => s.y], ["desc", "desc"])[0] as Datum;
                highestAcceptable.push(maxValue);
            }
        }
        return highestAcceptable.map(d => ({ x: d.x, y: d.y }));
    }

    const pefTrend = (): Serie[] => {
        if (!allSessions || allSessions.map(s => s.tests).flat().length === 0) return [];

        const highestAcceptableGroupedPerDay = groupBy(allSessions.map(s => ({
            x: moment(s.occurred).format("YYYY-MM-DD"),
            accepted: s.tests.length > 0 ? orderBy(s.tests, [a => a.accepted, b => b.fev1], ["desc", "desc"])[0].accepted : undefined,
            y: s.tests.length > 0 ? orderBy(s.tests, [a => a.accepted, b => b.pef], ["desc", "desc"])[0].pef : undefined
        })).filter(d => d.y), v => v.x);

        const highestAcceptable = extractHighestFromMultipleSessions(highestAcceptableGroupedPerDay);
        
        return [{
            id: "Highest PEF",
            color: "#ff0000",
            data: orderBy(highestAcceptable, [a => a.x], ["asc"]),
        }];
    }

    const memorizedPefTrend = React.useMemo(pefTrend, [allSessions]);

    const fev1Trend = (): Serie[] => {
        if (!allSessions || allSessions.map(s => s.tests).flat().length === 0) return [];

        const highestAcceptableGroupedPerDay = groupBy(allSessions.map(s => ({
            x: moment(s.occurred).format("YYYY-MM-DD"),
            accepted: s.tests.length > 0 ? orderBy(s.tests, [a => a.accepted, b => b.fev1], ["desc", "desc"])[0].accepted : undefined,
            y: s.tests.length > 0 ? orderBy(s.tests, [a => a.accepted, b => b.fev1], ["desc", "desc"])[0].fev1 : undefined,
        })).filter(d => d.y), v => v.x);

        const highestAcceptable = extractHighestFromMultipleSessions(highestAcceptableGroupedPerDay);

        return [{
            id: "Highest FEV1",
            color: "#ff0000",
            data: orderBy(highestAcceptable, [a => a.x], ["asc"])
        }];
    }

    const memorizedfev1Trend = React.useMemo(fev1Trend, [allSessions]);

    if (!subject || !allSessions) {
        return <LoadingWithMessage message="Loading Diary Results..." />
    }

    const calendarData = (): CalendarDatum[] => {
        const groupedSessions = groupBy(allSessions, s => moment(s.occurred).format("YYYY-MM-DD"));

        const calendarDays = [];
        for (const [k, v] of Object.entries(groupedSessions)) {
            // 0 = No data
            // 1 = Download
            // 2 = Tests
            // 4 = Questionnaire
            const normalisedValue = (v.flatMap(s => s.tests)?.length > 0 === true ? 2 : 1) + (v.flatMap(s => s.questionnaires)?.length > 0 === true ? 4 : 0);

            calendarDays.push({
                day: k,
                value: normalisedValue
            } as CalendarDatum);
        }

        return calendarDays;
    }

    const onCalendarClick = (datum: DiaryCalendarDayData) => {
        if (datum.color === "#fff")
            return;

        setSelectedDate(moment(datum.date).utc(true));
    };

    const onTrendClick = (e: Point) => {
        setSelectedDate(moment(e.data.x.toString()).utc(true));
    };

    const diaryTrendTabsContent = () => {
        return (
            <div className="tab-container">
                {currentTab === DiaryResultsTabs.PEF ? (
                    <>
                        {memorizedPefTrend.length > 0 ? <DiaryTrendGraph
                            data={memorizedPefTrend}
                            onPointClick={onTrendClick}
                            showFilter={showFilter}
                            onShowFilter={s => setShowFilter(s)}
                            tooltip={({ point }) => {
                                return <div className="lineChartTooltip">
                                    <strong>{moment(point.data.x).format("DD-MMM-YYYY")}</strong> PEF: <strong>{point.data.y}</strong>
                                </div>
                            }}
                            yAxisLabel="PEF (L/min)"
                            markers={subject.baselinePef ? [
                                {
                                    axis: 'y',
                                    value: subject.baselinePef,
                                    legend: 'Baseline PEF',
                                    lineStyle: {
                                        stroke: 'red',
                                    },
                                    textStyle: {
                                        fill: 'red',
                                    },
                                }
                            ] : undefined}
                            baseline={subject.baselinePef}
                        /> : <Alert show={true} category={AlertCategoryEnum.Info}>
                                There is no data to trend
                            </Alert>}
                    </>
                ) : null}
                {currentTab === DiaryResultsTabs.FEV1 ? (
                    <>
                        {memorizedfev1Trend.length > 0 ? <DiaryTrendGraph
                            data={memorizedfev1Trend}
                            onPointClick={onTrendClick}
                            showFilter={showFilter}
                            onShowFilter={s => setShowFilter(s)}
                            tooltip={({ point }) => {
                                return <div className="lineChartTooltip">
                                    <strong>{moment(point.data.x).format("DD-MMM-YYYY")}</strong> FEV1: <strong>{point.data.y}</strong>
                                </div>
                            }}
                            yAxisLabel="FEV1 (L)"
                        /> : <Alert show={true} category={AlertCategoryEnum.Info}>
                                There is no data to trend
                            </Alert>}
                    </>
                ) : null}
            </div>
        );
    }

    const pefTrendResults = pefTrend();

    const resultGraphsPanel = () => {
        if (allSessions.length === 0) {
            return <Alert show={true} category={AlertCategoryEnum.Info}>There are no Diary results for this subject</Alert>
        }

        return (
            <Authorization currentPermissions={currentUserPermissions} studyId={study.id} permissionId={PermissionEnum.ViewDiaryTrends}>
                <div className="panel panel-default" data-automationid="DiaryTrendPanel">
                    <div className="panel-body">
                        <ul className="nav nav-tabs">
                            <li className={classnames("nav-item", {
                                active: currentTab === DiaryResultsTabs.PEF
                            })}>
                                <a
                                    className="nav-link"
                                    onClick={() => setCurrentTab(DiaryResultsTabs.PEF)}
                                >
                                    PEF Trend
                                </a>
                            </li>
                            <li className={classnames("nav-item", {
                                active: currentTab === DiaryResultsTabs.FEV1
                            })}>
                                <a
                                    className="nav-link"
                                    onClick={() => setCurrentTab(DiaryResultsTabs.FEV1)}
                                >
                                    FEV1 Trend
                                </a>
                            </li>
                            <div className="pull-right">
                                <button className="btn btn-primary" disabled={pefTrendResults.length === 0} onClick={() => setShowFilter(true)}>
                                    <i className="fas fa-filter"></i>
                                </button>
                            </div>
                        </ul>
                        {diaryTrendTabsContent()}
                    </div>
                </div>
            </Authorization>
        );
    }
    
    const minYear = pefTrendResults.length !== 0 ? moment.min(...pefTrendResults[0].data.map(d => moment(d.x).startOf("year"))) : moment();
    const maxYear = pefTrendResults.length !== 0 ? moment.max(...pefTrendResults[0].data.map(d => moment(d.x).endOf("year"))) : moment();

    const calendarColorRange = ["#ffffff", "#006ba6", "#f1a332", "#f1a332", "#f1a332", "#f1a332", "#38a535"];

    const colorScale = (): ColorScale => {
        const defaultColorScale = scaleQuantize<string>()
            .domain([0, 6])
            .range(calendarColorRange);

        const intColorScale = value => {
            const number = parseInt(value)
            return defaultColorScale(number)
        }
        intColorScale.ticks = count => {
            return [0, 1, 2, 6];
        }

        return intColorScale;
    }

    const complianceSummary = () => {
        if (allSessions.length === 0) {
            return null;
        }

        const years = groupBy(allSessions, s => moment(s.occurred).format("YYYY"));
        const totalYears = allSessions ? Object.keys(years).length : 0;

        return (
            <div className="panel panel-default" data-automationid="DayByDaySummaryPanel">
                <div className="panel-heading">
                    <h4>Day by Day Summary</h4>
                </div>
                <div className="panel-body">
                    <div id="calendarWrapper" className="col-sm-12" style={{ height: (totalYears * 350) + "px" }}>
                        <DiaryDayByDay calendarData={calendarData}
                            minYear={minYear}
                            maxYear={maxYear}
                            calendarColorRange={calendarColorRange}
                            colorScale={colorScale}
                            onCalendarClick={onCalendarClick}
                        />
                    </div>
                </div>
            </div>
        );
    }

    const dates = groupBy(allSessions, s => moment(s.occurred).format("YYYY-MM-DD"));
    const totalDays = allSessions ? Object.keys(dates).length : 0;    

    return (
        <>
            <div className="row">
                <div className="col-sm-12">
                    <h2 className="text-primary">Diary Results</h2>
                </div>
            </div>

            <DiarySubjectSummaryPanel
                subjectId={subject.id}
                siteNumber={site ? site.number : ""}
                subjectNumber={subject.number}
                numberOfDays={totalDays}
                complianceRecords={complianceRecords}
            />

            {resultGraphsPanel()}
            
            {complianceSummary()}

            {selectedDate ? (
                <DiarySessionResult
                    subject={subject}
                    selectedDate={selectedDate.toISOString()}
                    sessions={sessions}
                    loadingScopedSessions={loadingSessions}
                    onClose={() => setSelectedDate(undefined)}
                />
            ) : null}
        </>
    );
}
